<template>
  <div
    class="relative flex-shrink-0"
    :class="{ 'cursor-not-allowed opacity-75': disabled }"
  >
    <div
      class="relative h-10 w-full pl-4 pr-12 flex items-center gap-2 border rounded cursor-pointer overflow-hidden"
      :class="[
        focus ? 'border-light-purple' : 'border-grey-200',
        disabled ? 'pointer-events-none bg-grey-100' : 'bg-pure-white'
      ]"
      @click="focus = true"
    >
      <div v-if="input.length === 0">
        <BlTextBody2 :text="placeholder" class="text-slate-600 truncate" />
      </div>
      <div v-else v-for="i in input" :key="i" class="flex">
        <BlChip v-if="getAttr(i, 'chip')" :chip="i" dense class="truncate" />
        <div
          v-else-if="getAttr(i, 'color')"
          class="h-7 max-w-32 px-2 flex items-center rounded truncate"
          :style="{ backgroundColor: getAttr(i, 'backgroundColor') || 'unset' }"
        >
          <BlTextBody2
            :text="getAttr(i, 'label')"
            :style="{ color: getAttr(i, 'color') || 'unset' }"
            class="truncate"
          />
        </div>
        <BlTextBody2
          v-else
          :text="getAttr(i, 'label') || i"
          class="text-slate-800 py-1 px-2 bg-slate-200 truncate max-w-32"
        />
      </div>
      <div
        class="absolute right-px w-9 h-10 py-2 px-2 transition-all duration-300"
        :class="[disabled ? 'bg-grey-100' : 'bg-pure-white']"
      >
        <BlIcon
          icon="chevron-down"
          class="w-5 h-5 transition-all duration-300"
          :class="[disabled ? 'pointer-events-none cursor-not-allowed' : '']"
          :style="{ rotate: focus ? '180deg' : '' }"
        />
      </div>
    </div>

    <div
      :data-id="id"
      v-if="!disabled && focus"
      class="absolute z-50 max-h-72 w-66 mt-2 py-2 px-2 flex flex-col gap-2 flex-shrink-0 bg-pure-white rounded border border-grey-200 shadow-md"
    >
      <div
        :data-id="id"
        class="px-4 py-2 flex items-center justify-between border-b border-slate-400"
      >
        <BlTextSubtitle2 :data-id="id" :text="label" />
        <div :data-id="id" class="cursor-pointer" @click="onClearAll">
          <BlTextSubtitle2
            :data-id="id"
            class="text-light-purple hover:text-dark-purple-200"
            text="Clear all"
          />
        </div>
      </div>

      <div v-if="search" :data-id="id" class="relative flex items-center">
        <input
          :data-id="id"
          :id="`search_field_${id}`"
          type="text"
          v-model="query"
          :placeholder="searchPlaceholder"
          class="py-2 px-4 w-full border border-grey-200 rounded focus:border-light-purple outline-none text-sm flex-shrink-0"
        />
        <BlIcon :data-id="id" icon="search" class="absolute right-2 w-4" />
      </div>
      <div
        v-if="filteredOptions.length === 0"
        :data-id="id"
        class="py-2 px-4 flex items-center justify-center"
      >
        <div v-if="fetchLoading" :data-id="id" class="blum-loader-sm"></div>
        <BlTextBody2 v-else :data-id="id" text="Option not found" />
      </div>
      <div v-else :data-id="id" class="px-2 flex flex-col overflow-y-auto">
        <div
          v-for="opt in filteredOptions"
          :key="opt.value"
          :data-id="id"
          class="py-2 flex items-center gap-2 flex-shrink-0 truncate cursor-pointer"
          :title="opt.label"
          @click="onSelect(opt)"
        >
          <div
            :data-id="id"
            class="bl-filter-checkbox"
            :class="{ 'checkbox-checked': isChecked(opt.value) }"
          >
            <BlIcon
              :data-id="id"
              class="bl-checkbox-icon"
              :class="[isChecked(opt.value) ? 'opacity-100' : 'opacity-0']"
              icon="check"
              color="secondary"
            />
          </div>
          <BlChip
            v-if="opt.chip"
            :data-id="id"
            :id="id"
            :chip="opt.value"
            dense
          />
          <div
            v-else-if="opt.color"
            :data-id="id"
            class="max-w-full h-7 px-2 flex items-center rounded truncate"
            :style="{ backgroundColor: opt.backgroundColor || 'unset' }"
          >
            <BlTextBody2
              :data-id="id"
              :text="opt.label"
              :style="{ color: opt.color || 'unset' }"
              class="truncate"
            />
          </div>
          <BlTextBody2
            v-else
            :data-id="id"
            :text="opt.label"
            class="truncate"
          />
        </div>
        <BlInputButton
          v-if="isShowMoreOption"
          :dataId="id"
          class="mt-2 hover:bg-hover-state"
          type="plain"
          label="See more ..."
          dense
          :loading="fetchLoading"
          @clicked="onMoreOption"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import debounce from "lodash/debounce";

import BlTextSubtitle2 from "./BlTextSubtitle2.vue";
import BlTextBody2 from "./BlTextBody2.vue";
import BlChip from "./BlChip.vue";
import BlIcon from "./BlIcon.vue";
import BlInputCheck from "./BlInputCheck.vue";
import BlInputButton from "./BlInputButton.vue";

export default {
  name: "BlSingelFilter",
  components: {
    BlTextSubtitle2,
    BlTextBody2,
    BlChip,
    BlIcon,
    BlInputCheck,
    BlInputButton
  },
  props: {
    disabled: { type: Boolean, default: false },
    label: { type: String },
    placeholder: { type: String },
    value: { default: () => [] }, // { value, label, chip?: boolean }[]
    options: { default: () => [] }, // { value, label, chip?: boolean }[]
    search: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Search ..." },
    fetchProperty: {
      type: Object,
      default: () => ({
        url: "",
        dataKey: "",
        countKey: "total_count",
        optionKeys: { value: "value", label: "label" }
      })
    }
    // @change(value)
  },
  data() {
    return {
      input: [],
      stateOptions: [],
      focus: false,
      query: "",
      fetchLoading: false,
      isShowMoreOption: false,
      fetchPage: 1
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-single-filter-${combination}`;
    },
    filteredOptions() {
      if (!this.query) return this.stateOptions;
      return this.stateOptions.filter(i => {
        const lowerKey = this.query.toLowerCase();
        const lowerLabel = i.label.toLowerCase();
        return lowerLabel.includes(lowerKey);
      });
    }
  },
  watch: {
    focus(val) {
      if (val) {
        if (this.fetchProperty.url) {
          this.onFetchOptions();
        }
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
          if (this.search) {
            const field = document.getElementById(`search_field_${this.id}`);
            if (field) field.focus();
          }
        }, 10);
      } else {
        this.$emit("change", this.input);
      }
    },
    query(val) {
      this.fetchLoading = true;
      this.onSearch(val);
    },
    value: {
      immediate: true,
      handler(val) {
        this.input = val;
      }
    },
    options: {
      deep: true,
      immediate: true,
      handler(val) {
        const lastArr = [...this.stateOptions, ...val];
        const uniqueArr = this.removeDuplicates(lastArr, "value");
        this.stateOptions = uniqueArr;
      }
    }
  },
  mounted() {
    if (this.fetchProperty.url) {
      this.onFetchOptions();
    }
  },
  methods: {
    ...mapActions(["actionGet"]),
    removeDuplicates(arr, key) {
      const seen = new Set();
      return arr.filter(item => {
        const val = item[key];
        if (seen.has(val)) {
          return false;
        }
        seen.add(val);
        return true;
      });
    },
    async onFetchOptions() {
      const url = this.fetchProperty.url;
      const query = this.query;
      const page = this.fetchPage;
      const dataKey = this.fetchProperty.dataKey;
      const countKey = this.fetchProperty.countKey;
      const keys = this.fetchProperty.optionKeys;
      const options = [...this.stateOptions];

      const params = { url: `${url}${query}` };
      params.url += `&per_page=${50}`;
      if (query) params.url += `&page=${1}`;
      else params.url += `&page=${page}`;

      this.fetchLoading = true;
      const { success, data } = await this.actionGet(params);
      this.fetchLoading = false;
      if (!success) return;
      if (!dataKey) return;

      const incomingArr = data[dataKey].map(i => {
        const opt = {
          value: i[keys.value],
          label: i[keys.label]
        };
        if (keys.color) opt.color = i[keys.color];
        if (keys.backgroundColor) opt.backgroundColor = i[keys.backgroundColor];
        return opt;
      });

      const lastArr = [...options, ...incomingArr];
      const uniqueArr = this.removeDuplicates(lastArr, "value");

      this.isShowMoreOption = true;
      if (uniqueArr.length >= data[countKey]) {
        this.isShowMoreOption = false;
      }

      this.stateOptions = uniqueArr;
    },
    onMoreOption() {
      this.fetchPage += 1;
      this.onFetchOptions();
    },
    //
    getAttr(val, key) {
      const found = this.stateOptions.find(i => i.value === val);
      return found ? found[key] || "" : "";
    },
    //
    onSearch: debounce(function(e = "") {
      if (this.fetchProperty.url) {
        this.onFetchOptions();
      } else {
        this.fetchLoading = false;
      }
      this.$emit("search", e);
    }, 1000),
    isChecked(val) {
      return this.input.includes(val);
    },
    onSelect(opt) {
      let arr = [...this.input];
      const opts = this.filteredOptions.map(i => i.value);
      const remove = arr.includes(opt.value);
      if (remove) {
        arr = arr.filter(i => i !== opt.value);
      } else {
        arr.push(opt.value);
      }
      arr.sort((a, b) => opts.indexOf(a) - opts.indexOf(b));
      this.input = arr;
    },
    onClearAll() {
      this.input = [];
    },
    //
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.focus = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bl-filter-checkbox {
  cursor: pointer;
  position: relative;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 2px;
  border: 1px solid var(--border-color);
  background: var(--background-depth-one-color);
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  &.checkbox-checked {
    background: var(--primary-color);
    border-color: var(--primary-color);
  }
  .bl-checkbox-icon {
    width: 1.125rem;
    height: 1.125rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BlSingleFilter.vue